'use client';

import { clusterByGrid as clusterByGridMethod } from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { useMaps } from '@/fo/ui/mod/yandex/maps';

import { MapContent } from '../components';
import { Info } from '../components/Info/Info';
import { PointInfo } from '../components/PointInfo/PointInfo';
import { PointSelector } from '../components/PointSelector/PointSelector';
import { DEFAULT_CLUSTER_GRID_SIZE } from '../consts';
import { getStorePoints } from '../helpers/getPointsByTypes';
import { useMapController, usePointsController } from '../hooks';
import { StorePoint, PointFeature } from '../types';

import { styles } from './Map.styles';

interface DefaultProps {
  points: StorePoint[];
  variant?: 'default';
}

export type MapProps = DefaultProps & {
  pointSelectorSize?: 'small' | 'default';
  selectedCity?: string;
  /** Начальный zoom карты */
  zoom?: number;
};

export function Map({
  pointSelectorSize = 'default',
  selectedCity,
  zoom,
  ...props
}: MapProps) {
  const slots = styles();

  const { reactifyApi, clusterer } = useMaps();

  const isDefaultVariant = !props.variant || props.variant === 'default';
  const {
    visiblePoints,
    selectedPointType,
    handlePointTypeChange,
    pointTypes
  } = usePointsController(isDefaultVariant ? props.points : []);
  const pointsList = useMemo(() => {
    if (!isDefaultVariant || selectedCity) {
      return getStorePoints(props.points);
    }

    return visiblePoints.flatMap(({ points }) => points);
  }, [isDefaultVariant, props.points, selectedCity, visiblePoints]);

  const centerMapPoint = pointsList?.[0]?.geometry?.coordinates ?? [0, 0];

  const {
    location,
    selectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleMapUpdate
  } = useMapController(centerMapPoint, zoom);
  const [openDrawer, setOpenDrawer] = useState(false);

  const isDesktop = useMediaQuery('lg');

  useEffect(() => {
    if (!selectedPoint) {
      setOpenDrawer(false);
    }
  }, [selectedPoint]);

  const gridSizedMethod = useMemo(() => {
    if (!clusterer) {
      return null;
    }

    const clusterByGrid =
      clusterer.clusterByGrid as unknown as typeof clusterByGridMethod;

    return clusterByGrid({ gridSize: DEFAULT_CLUSTER_GRID_SIZE });
  }, [clusterer]);

  const onMapMarkerClickWithDrawer = useCallback(
    (feature: PointFeature) => {
      const result = handleMarkerClick(true)(feature);

      if (!isDesktop && result.isMap) {
        setOpenDrawer(true);
      }
    },
    [handleMarkerClick, isDesktop, setOpenDrawer]
  );

  if (!reactifyApi || !clusterer || !gridSizedMethod) {
    return null;
  }

  const pointSelector = (
    <PointSelector
      size={pointSelectorSize}
      key="point-selector"
      selectedPointType={selectedCity ?? selectedPointType}
      pointTypes={pointTypes}
      onPointTypeChange={handlePointTypeChange}
    />
  );

  const info = <Info points={pointsList}></Info>;
  const mapNode = (
    <MapContent
      key="map-content"
      reactifyApi={reactifyApi}
      clusterer={clusterer}
      gridSizedMethod={gridSizedMethod}
      pointsList={pointsList}
      selectedPoint={selectedPoint}
      location={location}
      isDesktop={isDesktop}
      onMarkerClick={onMapMarkerClickWithDrawer}
      onClusterClick={handleClusterClick}
      onMapUpdate={handleMapUpdate}
      selectedCity={selectedCity}
    />
  );

  if (isDesktop) {
    return (
      <>
        {!selectedCity && pointSelector}
        <div className={slots.base()}>{mapNode}</div>
        {!selectedCity && info}
      </>
    );
  }

  return (
    <>
      <UiKitDrawerBase
        title=""
        open={openDrawer && !!selectedPoint}
        onOpenChange={setOpenDrawer}
        direction="bottom"
        closeButtonClassName="opacity-0"
      >
        {selectedPoint && (
          <PointInfo
            point={selectedPoint}
            selectedCity={selectedCity}
          />
        )}
      </UiKitDrawerBase>
      {!selectedCity && pointSelector}
      <div className={slots.panelContent()}>
        <div className={slots.panelContent()}>{mapNode}</div>
      </div>
      {!selectedCity && info}
    </>
  );
}
